import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, 
  FileText, 
  Award, 
  CreditCard,
  TrendingUp,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, getAuthHeader());
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[#F5A623] animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Applications',
      value: stats?.total_applications || 0,
      icon: FileText,
      color: 'bg-blue-500'
    },
    {
      title: 'Pending Applications',
      value: stats?.pending_applications || 0,
      icon: FileText,
      color: 'bg-amber-500'
    },
    {
      title: 'Active Members',
      value: stats?.active_members || 0,
      icon: Users,
      color: 'bg-green-500'
    },
    {
      title: 'Certificates Issued',
      value: stats?.active_members || 0,
      icon: Award,
      color: 'bg-purple-500'
    },
    {
      title: 'Total Payments',
      value: stats?.total_payments || 0,
      icon: CreditCard,
      color: 'bg-indigo-500'
    },
    {
      title: 'Total Revenue',
      value: `€${(stats?.total_revenue || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: 'bg-emerald-500'
    },
  ];

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0E285B] mb-8">
        Admin Dashboard
      </h1>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={index}
              className="bg-white p-6 rounded-sm border border-slate-200 shadow-sm"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="font-['Playfair_Display'] text-3xl font-bold text-[#0E285B]">
                    {stat.value}
                  </p>
                </div>
                <div className={`h-12 w-12 ${stat.color} rounded-sm flex items-center justify-center`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-sm border border-slate-200 shadow-sm">
        <h2 className="font-semibold text-[#0E285B] mb-4">Quick Actions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <a 
            href="/admin/applications" 
            className="p-4 border border-slate-200 rounded-sm hover:border-[#F5A623] hover:bg-slate-50 transition-colors"
          >
            <FileText className="h-6 w-6 text-[#F5A623] mb-2" />
            <p className="font-medium text-[#0E285B]">Review Applications</p>
            <p className="text-sm text-slate-500">{stats?.pending_applications || 0} pending</p>
          </a>
          <a 
            href="/admin/members" 
            className="p-4 border border-slate-200 rounded-sm hover:border-[#F5A623] hover:bg-slate-50 transition-colors"
          >
            <Users className="h-6 w-6 text-[#F5A623] mb-2" />
            <p className="font-medium text-[#0E285B]">Manage Members</p>
            <p className="text-sm text-slate-500">{stats?.active_members || 0} active</p>
          </a>
          <a 
            href="/admin/certificates" 
            className="p-4 border border-slate-200 rounded-sm hover:border-[#F5A623] hover:bg-slate-50 transition-colors"
          >
            <Award className="h-6 w-6 text-[#F5A623] mb-2" />
            <p className="font-medium text-[#0E285B]">Certificates</p>
            <p className="text-sm text-slate-500">View all certificates</p>
          </a>
          <a 
            href="/admin/payments" 
            className="p-4 border border-slate-200 rounded-sm hover:border-[#F5A623] hover:bg-slate-50 transition-colors"
          >
            <CreditCard className="h-6 w-6 text-[#F5A623] mb-2" />
            <p className="font-medium text-[#0E285B]">Payments</p>
            <p className="text-sm text-slate-500">View transactions</p>
          </a>
        </div>
      </div>
    </div>
  );
}
